<template>
  <div v-if="isBare" class="app app-bare">
    <router-view></router-view>
  </div>

  <div v-else class="app">

    <Navbar v-model:collapsed="isNavbarCollapsed" class="navigation"/>

    <div :class="['main-app', { 'main-app-shrunk': isNavbarCollapsed }]">
      <div v-if="isImpersonating" class="impersonation-banner">
        <span class="imp-text">
          Просмотр как <strong>{{ impersonationName }}</strong>
        </span>
        <button class="imp-end-btn" type="button" @click="endImpersonation">Завершить просмотр</button>
      </div>
      <TmsIntakeBanner v-if="canReportToIt" />
      <Header class="header-plank" />
      <div class="content-scroll">
        <router-view></router-view>
      </div>
    </div>

    <Trip360Popup v-if="canTrip360" />
    <SupportButton v-if="canReportToIt" />
    <ConfirmGate v-if="canReportToIt" />
  </div>
</template>


<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import Navbar from '@/components/Navbar.vue'
import Header from '@/components/Header.vue'
import Trip360Popup from '@/components/trip360/Trip360Popup.vue'
import SupportButton from '@/components/workspace/SupportButton.vue'
import TmsIntakeBanner from '@/components/system/TmsIntakeBanner.vue'
import ConfirmGate from '@/components/system/ConfirmGate.vue'
import { useTrip360Store } from '@/stores/trip360'
import { parseTrip360Hash } from '@/components/trip360/trip360Logic.mjs'

export default{
  name:'App',
  components:{
    Navbar,
    Header,
    Trip360Popup,
    SupportButton,
    TmsIntakeBanner,
    ConfirmGate,
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const trip360 = useTrip360Store()
    // Состояние сайдбара переживает перезагрузку страницы
    const NAVBAR_COLLAPSED_KEY = 'navbarCollapsed'
    // На телефоне меню всегда начинается закрытым (шторка), на компьютере — как оставил человек.
    const isPhone = Boolean(window.matchMedia && window.matchMedia('(max-width: 768px)').matches)
    const isNavbarCollapsed = ref(isPhone || window.localStorage.getItem(NAVBAR_COLLAPSED_KEY) === '1')
    watch(isNavbarCollapsed, (value) => {
      if (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) return
      try {
        window.localStorage.setItem(NAVBAR_COLLAPSED_KEY, value ? '1' : '0')
      } catch (e) {
        /* localStorage может быть недоступен — молча игнорируем */
      }
    })

    // Standalone-страницы (meta.bare) рендерятся без Navbar/Header/попапов.
    const isBare = computed(() => route.meta.bare === true)

    const isImpersonating = computed(() => store.getters.isImpersonating)
    const impersonationName = computed(() => store.getters.impersonationName)
    const endImpersonation = () => store.dispatch('endImpersonation')
    const canTrip360 = computed(() => store.getters.hasPermission('trip360.view'))
    // Кнопка «Сообщить в IT» — всем вошедшим сотрудникам (не гостям).
    const canReportToIt = computed(() => {
      const role = store.state.user?.user?.role?.name
      return Boolean(role) && role !== 'Гость'
    })

    // Deep-link: /#trip360=<ключ> открывает 360-окно с любого места сайта.
    const handleTrip360Hash = () => {
      if (isBare.value) return
      const key = parseTrip360Hash(window.location.hash)
      if (key) {
        trip360.openTrip360(key)
        window.history.replaceState(null, '', window.location.pathname + window.location.search)
      }
    }

    onMounted(() => {
      handleTrip360Hash()
      window.addEventListener('hashchange', handleTrip360Hash)
    })
    onBeforeUnmount(() => {
      window.removeEventListener('hashchange', handleTrip360Hash)
    })

    return {
      isNavbarCollapsed,
      isBare,
      isImpersonating,
      impersonationName,
      endImpersonation,
      canTrip360,
      canReportToIt,
    }
  },
}
</script>


<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.app {
  display: flex;
  justify-content: flex-start;
  align-items: stretch;
  font-family: ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica Neue,Arial,Noto Sans,sans-serif,Apple Color Emoji,Segoe UI Emoji,Segoe UI Symbol,Noto Color Emoji;
  -webkit-font-smoothing: antialiased;
  height: 100vh;
  background-color: #fff;
  overflow: hidden;
}

/* Standalone-страницы (meta.bare): без flex-сжатия, контент занимает всё окно. */
.app-bare {
  display: block;
}

.main-app {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 280px;
  height: 100vh;
  overflow: hidden;
  transition: margin-left 0.3s ease;
}

.main-app-shrunk {
  margin-left: 76px;
}

.content-scroll {
  flex: 1;
  overflow-y: auto;
}

.liner {
  border-bottom: 1px solid rgba(255,148,29, 1.0);
}

.bg-denied {
  background-color: rgba(182,0,24,0.8) !important;
}
.bg-success {
  background-color: rgba(145,218,85,1);
}
.bg-neutral {
  background-color:rgba(0,123,255,1);
  color: white;
}


.header-plank {
  width: 100%;
  flex-shrink: 0;
}

.impersonation-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #fef3c7;
  border-bottom: 1px solid #f59e0b;
  padding: 8px 20px;
  font-size: 14px;
  color: #78350f;
  flex-shrink: 0;
}

.imp-text strong {
  font-weight: 700;
}

.imp-end-btn {
  background: #b45309;
  border: none;
  border-radius: 6px;
  color: #fff;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  white-space: nowrap;
}

.imp-end-btn:hover {
  background: #92400e;
}

.font-16 {
  font-size: 16px;
}

.marginbottom-15 {
  margin-bottom: 15px;
}

@media screen and (max-width: 426px) {
  .main-app {
    margin-left: 0;
  }
}
</style>
